import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

// Dismiss the bootstrap splash the moment we know the auth state. One call,
// idempotent (the inline script in index.html guards against duplicates).
const dismissBootSplash = () => {
  if (typeof window !== "undefined" && typeof window.__zaviBootHide === "function") {
    window.__zaviBootHide();
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out

  useEffect(() => {
    let cancelled = false;
    const fallback = setTimeout(dismissBootSplash, 1500);
    api.get("/auth/me")
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) setUser(false); })
      .finally(() => { clearTimeout(fallback); if (!cancelled) dismissBootSplash(); });
    return () => { cancelled = true; clearTimeout(fallback); };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed", err);
    }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, login, logout }), [user, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const RequireAuth = ({ role, children }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="auth-loading">
        <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-semibold animate-pulse">Loading...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    const home = user.role === "admin" ? "/admin" : user.role === "coach" ? "/coach" : "/portal";
    return <Navigate to={home} replace />;
  }
  return children;
};
